import axios from 'axios';

// Use relative URL to leverage the proxy in development
// In production, this should be set via REACT_APP_API_URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 120000, // 2 minutes par defaut pour les scans
  headers: { 'Content-Type': 'application/json' }
});

// Retry configuration for transient network errors
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second base delay

// Track last network error toast to prevent spam
let lastNetworkErrorToast = 0;
const NETWORK_ERROR_TOAST_COOLDOWN = 10000; // 10 seconds between toasts

// Track online/offline status for graceful handling of network changes
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
let offlineToastShown = false;

// Listen for online/offline events to handle ERR_NETWORK_CHANGED gracefully
if (typeof window !== 'undefined') {
  window.addEventListener('online', async () => {
    isOnline = true;
    offlineToastShown = false;
    const toast = await getToast();
    if (toast) {
      toast.success('Connection restored', { duration: 3000 });
    }
    console.log('[API] Network connection restored');
  });

  window.addEventListener('offline', async () => {
    isOnline = false;
    if (!offlineToastShown) {
      offlineToastShown = true;
      const toast = await getToast();
      if (toast) {
        toast.warning('Connection lost. Requests will resume automatically.', { duration: 5000 });
      }
    }
    console.log('[API] Network connection lost');
  });
}

// Lazy import toast to avoid circular dependencies
let toastModule = null;
const getToast = async () => {
  if (!toastModule) {
    try {
      toastModule = await import('../../utils/toastUtils.js');
    } catch (e) {
      console.warn('Toast module not available');
      return null;
    }
  }
  return toastModule.toast || toastModule.default;
};

// Show user-friendly network error notification (rate-limited)
const showNetworkErrorToast = async (isNetworkChanged = false) => {
  const now = Date.now();
  if (now - lastNetworkErrorToast < NETWORK_ERROR_TOAST_COOLDOWN) {
    return; // Skip if we showed a toast recently
  }
  lastNetworkErrorToast = now;

  const toast = await getToast();
  if (toast) {
    const message = isNetworkChanged
      ? 'Unstable network connection. Check your internet connection.'
      : 'Network error. Attempting to reconnect...';
    toast.warning(message, { duration: 5000 });
  }
};

// Helper to check if error is a retryable network error
const isNetworkError = (error) => {
  return (
    !error.response && // No response from server
    error.code !== 'ECONNABORTED' && // Not a timeout (handled separately)
    (!error.message ||
      error.message.includes('Network Error') ||
      error.message.includes('ERR_NETWORK') ||
      error.message.includes('ERR_CONNECTION') ||
      error.message.includes('ERR_INTERNET_DISCONNECTED') ||
      error.message.includes('ERR_NETWORK_CHANGED'))
  );
};

// Helper to check if error is a retryable server error (503 Service Unavailable)
const isRetryableServerError = (error) => {
  return error.response && error.response.status === 503;
};

// Helper to check if it's specifically a network changed error
const isNetworkChangedError = (error) => {
  return error.message && error.message.includes('ERR_NETWORK_CHANGED');
};

// Helper to check if request method is idempotent (safe to retry)
const isIdempotentRequest = (config) => {
  const method = (config.method || 'get').toLowerCase();
  return ['get', 'head', 'options', 'put', 'delete'].includes(method);
};

// Helper to wait for network to come back online (max wait time configurable)
// minStabilizeMs ensures a minimum wait even if already online (for network change recovery)
const waitForOnline = (maxWaitMs = 30000, minStabilizeMs = 0) => {
  return new Promise((resolve, reject) => {
    // If online and no stabilization needed, resolve immediately
    if (isOnline && minStabilizeMs === 0) {
      resolve();
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const stabilized = elapsed >= minStabilizeMs;

      if (isOnline && stabilized) {
        clearInterval(checkInterval);
        resolve();
      } else if (elapsed > maxWaitMs) {
        clearInterval(checkInterval);
        reject(new Error('Network offline timeout'));
      }
    }, 500);
  });
};

// Retry interceptor for network errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Initialize retry count
    config.__retryCount = config.__retryCount || 0;

    // Check if this is a network error or retryable server error
    const networkError = isNetworkError(error);
    const networkChanged = isNetworkChangedError(error);
    const serverError = isRetryableServerError(error);

    // Check if we should retry (network errors or 503 Service Unavailable)
    const shouldRetry =
      (networkError || serverError) &&
      isIdempotentRequest(config) &&
      config.__retryCount < MAX_RETRIES;

    if (shouldRetry) {
      config.__retryCount += 1;

      // Exponential backoff: 1s, 2s, 4s
      const delay = RETRY_DELAY * Math.pow(2, config.__retryCount - 1);

      const errorType = serverError ? 'Server unavailable (503)' : 'Network error';
      console.warn(
        `[API] ${errorType}, retrying (${config.__retryCount}/${MAX_RETRIES}) in ${delay}ms:`,
        config.url
      );

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));

      // If offline due to network change, wait for connection to restore
      // ERR_NETWORK_CHANGED can occur when browser still reports online, so wait anyway
      // Skip this for server errors (503) as the network is fine
      if (!serverError && (!isOnline || networkChanged)) {
        try {
          // Longer wait for network changed errors since the network is actively switching
          // Use stabilization time to ensure network is stable after a change
          const waitTime = networkChanged ? 15000 : 10000;
          const stabilizeTime = networkChanged ? 2000 : 0;
          await waitForOnline(waitTime, stabilizeTime);
        } catch (e) {
          // Continue with retry even if still offline
          // For network changed, add extra stabilization delay
          if (networkChanged) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      // Retry the request
      return apiClient(config);
    }

    // If retries exhausted for network errors, show user-friendly toast
    if (networkError && config.__retryCount >= MAX_RETRIES) {
      showNetworkErrorToast(networkChanged);
    }

    // If retries exhausted for 503 errors OR non-idempotent request with 503, show user-friendly toast
    if (serverError && (config.__retryCount >= MAX_RETRIES || !isIdempotentRequest(config))) {
      getToast().then(toast => {
        if (toast) {
          toast.warning('Service temporarily unavailable. Please try again in a moment.', { duration: 5000 });
        }
      });
    }

    // If not retrying, reject with original error
    return Promise.reject(error);
  }
);

// Request interceptor
apiClient.interceptors.request.use(
  async (config) => {
    // If offline, wait briefly for network to come back (handles ERR_NETWORK_CHANGED)
    if (!isOnline) {
      try {
        await waitForOnline(5000); // Wait up to 5 seconds for network
      } catch (e) {
        // Network still offline, let the request proceed and fail naturally
        console.warn('[API] Network offline, request may fail:', config.url);
      }
    }

    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for auth and additional error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Invalid or expired token - redirect to login
      localStorage.removeItem('access_token');
      // Don't redirect if already on a public page
      const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/magic-login', '/share', '/sign', '/verify-email', '/setup', '/accept-invitation', '/invitation', '/auth-redirect', '/dedicated-server', '/security/verify-login', '/pricing'];
      const currentPath = window.location.pathname;
      // Don't redirect for background tracking requests
      const isBackgroundTracking = error.config?.url?.includes('/history/batch') || error.config?.url?.includes('/history/track');
      if (!isBackgroundTracking && !publicPaths.some(path => currentPath.startsWith(path))) {
        window.location.href = '/login';
        // Reject with a special error to allow error handlers to clean up loading states
        // The redirect will happen, but components can properly handle the error
        const authError = new Error('Authentication required - redirecting to login');
        authError.isAuthRedirect = true;
        authError.response = error.response;
        return Promise.reject(authError);
      }
    }

    // Add meaningful error message for 503 errors to help with debugging
    if (error.response?.status === 503) {
      error.message = error.message || 'Service temporairement indisponible (503)';
    }

    return Promise.reject(error);
  }
);

// Auth API
export const auth = {
  login: async (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    const response = await apiClient.post('/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
    return response.data;
  },
  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error.message);
    } finally {
      localStorage.removeItem('access_token');
    }
  },
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  me: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  isAuthenticated: () => !!localStorage.getItem('access_token'),
  /**
   * Checks if the token is valid client-side (exists and not expired).
   * This avoids making API calls with expired tokens that would return 401.
   * @returns {boolean} true if token exists and is not expired
   */
  isTokenValidClientSide: () => {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (exp && Date.now() >= exp * 1000) {
        // Token is expired, remove it
        localStorage.removeItem('access_token');
        return false;
      }
      return true;
    } catch {
      // If token parsing fails, it's invalid
      localStorage.removeItem('access_token');
      return false;
    }
  },
};

// Scans API
export const scans = {
  create: async (scanData) => {
    const response = await apiClient.post('/scan', scanData);
    return response.data;
  },
  list: async (skip = 0, limit = 10, workspaceId = null) => {
    const params = { skip, limit };
    if (workspaceId) params.workspace_id = workspaceId;
    const response = await apiClient.get('/scan', { params });
    return response.data;
  },
  get: async (scanId) => {
    const response = await apiClient.get(`/scan/${scanId}`);
    return response.data;
  },
  delete: async (scanId) => {
    await apiClient.delete(`/scan/${scanId}`);
  },
  getHosts: async (scanId) => {
    const response = await apiClient.get(`/scan/${scanId}/hosts`);
    return response.data;
  },
  getVulnerabilities: async (scanId) => {
    const response = await apiClient.get(`/scan/${scanId}/vulnerabilities`);
    return response.data;
  },
  getStats: async (scanId) => {
    const response = await apiClient.get(`/scan/${scanId}/stats`);
    return response.data;
  },
  validateForReport: async (scanIds, validated = true) => {
    const response = await apiClient.post('/scan/validate-for-report', {
      scan_ids: scanIds,
      validated
    });
    return response.data;
  },
  getValidated: async () => {
    const response = await apiClient.get('/scan/validated');
    return response.data;
  },
  stop: async (scanId) => {
    const response = await apiClient.post(`/scan/${scanId}/stop`);
    return response.data;
  },
  resume: async (scanId) => {
    const response = await apiClient.post(`/scan/${scanId}/resume`);
    return response.data;
  },
  // Rescan multiple scans with original parameters
  rescan: async (scanIds) => {
    const response = await apiClient.post('/scan/rescan', {
      scan_ids: scanIds
    });
    return response.data;
  },
};

// Dashboard API
export const dashboard = {
  getStats: async (timeframe = '7d') => {
    const response = await apiClient.get('/dashboard/stats', {
      params: { timeframe }
    });
    return response.data;
  },
  getActivity: async () => {
    const response = await apiClient.get('/dashboard/activity');
    return response.data;
  },
};

// Intelligence API
export const intelligence = {
  mlPredict: async (scanId) => {
    const response = await apiClient.post(`/intelligence/scan/${scanId}/ml-predict`);
    return response.data;
  },
  getThreatIntel: async (scanId) => {
    const response = await apiClient.get(`/intelligence/scan/${scanId}/threat-intel`);
    return response.data;
  },
  getAttackChains: async () => {
    const response = await apiClient.get('/intelligence/attack-chains');
    return response.data;
  },
  getRiskScore: async (scanId) => {
    const response = await apiClient.get(`/intelligence/analytics/risk-score/${scanId}`);
    return response.data;
  },
};

// Reports API
export const reports = {
  generate: async (scanId, format = 'html') => {
    const response = await apiClient.post(`/reports/scan/${scanId}/generate`, null, {
      params: { format },
      responseType: format === 'pdf' ? 'blob' : 'json'
    });
    return response.data;
  },
  getCompliance: async (scanId, standard = 'OWASP_TOP_10') => {
    const response = await apiClient.get(`/reports/scan/${scanId}/compliance/${standard}`);
    return response.data;
  },
  // List Gamma-generated report files
  listGammaReports: async () => {
    const response = await apiClient.get('/reports/list');
    return response.data;
  },
  // Download a Gamma report file
  downloadGammaReport: async (filename) => {
    const response = await apiClient.get(`/reports/download/${encodeURIComponent(filename)}`, {
      responseType: 'blob'
    });
    return response.data;
  },
  // Generate report from natural language prompt
  generateFromPrompt: async (prompt, workspaceId = null) => {
    const response = await apiClient.post('/reports/generate-from-prompt', {
      prompt,
      workspace_id: workspaceId
    });
    return response.data;
  },
};

// Health API
export const health = {
  check: async () => {
    const response = await apiClient.get('/health');
    return response.data;
  },
};

// System API
export const system = {
  getResources: async () => {
    const response = await apiClient.get('/system/resources');
    return response.data;
  },
  getStatus: async () => {
    const response = await apiClient.get('/system/status');
    return response.data;
  },
};

// Notifications API
export const notifications = {
  list: async (limit = 50, offset = 0, unreadOnly = false, types = null) => {
    const params = { limit, offset, unread_only: unreadOnly };
    if (types) params.types = types;
    const response = await apiClient.get('/notifications', { params });
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await apiClient.get('/notifications/unread-count');
    return response.data;
  },
  markAsRead: async (notificationId) => {
    const response = await apiClient.post(`/notifications/${notificationId}/read`);
    return response.data;
  },
  markAllAsRead: async () => {
    const response = await apiClient.post('/notifications/read-all');
    return response.data;
  },
  dismiss: async (notificationId) => {
    const response = await apiClient.delete(`/notifications/${notificationId}`);
    return response.data;
  },
  clearOld: async () => {
    const response = await apiClient.delete('/notifications');
    return response.data;
  },
};

// OT/ICS/SCADA Scanning API
export const otScan = {
  // Get available protocols
  getProtocols: async () => {
    const response = await apiClient.get('/ot/protocols');
    return response.data;
  },
  // Get risk warning message
  getRiskWarning: async () => {
    const response = await apiClient.get('/ot/risk-warning');
    return response.data;
  },
  // Create a new OT scan
  create: async (scanData) => {
    const response = await apiClient.post('/ot/scan', scanData);
    return response.data;
  },
  // Get OT scan by ID
  get: async (scanId) => {
    const response = await apiClient.get(`/ot/scan/${scanId}`);
    return response.data;
  },
  // List all OT scans
  list: async (skip = 0, limit = 50) => {
    const response = await apiClient.get('/ot/scans', { params: { skip, limit } });
    return response.data;
  },
  // Delete OT scan
  delete: async (scanId) => {
    const response = await apiClient.delete(`/ot/scan/${scanId}`);
    return response.data;
  },
  // Stop OT scan
  stop: async (scanId) => {
    const response = await apiClient.post(`/ot/scan/${scanId}/stop`);
    return response.data;
  },
  // Get scan report
  getReport: async (scanId) => {
    const response = await apiClient.get(`/ot/scan/${scanId}/report`);
    return response.data;
  },
  // Get OT tools
  getTools: async () => {
    const response = await apiClient.get('/ot/tools');
    return response.data;
  },
};

// IoT Scanning API
export const iotScan = {
  // Get available protocols
  getProtocols: async () => {
    const response = await apiClient.get('/iot/protocols');
    return response.data;
  },
  // Get risk warning message
  getRiskWarning: async () => {
    const response = await apiClient.get('/iot/risk-warning');
    return response.data;
  },
  // Create a new IoT scan
  create: async (scanData) => {
    const response = await apiClient.post('/iot/scan', scanData);
    return response.data;
  },
  // Get IoT scan by ID
  get: async (scanId) => {
    const response = await apiClient.get(`/iot/scan/${scanId}`);
    return response.data;
  },
  // List all IoT scans
  list: async (skip = 0, limit = 50) => {
    const response = await apiClient.get('/iot/scans', { params: { skip, limit } });
    return response.data;
  },
  // Delete IoT scan
  delete: async (scanId) => {
    const response = await apiClient.delete(`/iot/scan/${scanId}`);
    return response.data;
  },
  // Stop IoT scan
  stop: async (scanId) => {
    const response = await apiClient.post(`/iot/scan/${scanId}/stop`);
    return response.data;
  },
  // Get scan report
  getReport: async (scanId) => {
    const response = await apiClient.get(`/iot/scan/${scanId}/report`);
    return response.data;
  },
  // Get IoT tools
  getTools: async () => {
    const response = await apiClient.get('/iot/tools');
    return response.data;
  },
};

// Cloud Security Scan API
export const cloudScan = {
  create: async (scanData) => {
    const response = await apiClient.post('/scan', scanData);
    return response.data;
  },
  getCompliances: (provider) => {
    const map = {
      aws: [
        { value: 'cis_level1_aws_2.0', label: 'CIS Level 1 AWS' },
        { value: 'cis_level2_aws_2.0', label: 'CIS Level 2 AWS (recommended)' },
        { value: 'aws_well_architected_framework_security_pillar', label: 'AWS Well-Architected' },
        { value: 'nist_800_53_revision_5_aws', label: 'NIST 800-53 Rev5' },
        { value: 'iso27001_2013_aws', label: 'ISO 27001:2013' },
      ],
      azure: [
        { value: 'cis_ms_azure_2.0', label: 'CIS Azure 2.0 (recommended)' },
        { value: 'azure_security_benchmark_v3', label: 'Azure Security Benchmark v3' },
        { value: 'iso27001_2013_azure', label: 'ISO 27001:2013' },
      ],
      gcp: [
        { value: 'cis_gcp_2.0', label: 'CIS GCP 2.0 (recommended)' },
        { value: 'gcp_audit_manager', label: 'GCP Audit Manager' },
      ],
      ovh: [
        { value: 'iso27001_ovh', label: 'ISO 27001:2022 (recommended)' },
        { value: 'nis2_ovh', label: 'NIS2 European' },
        { value: 'hds_ovh', label: 'HDS — Health Data Host' },
      ],
      scaleway: [
        { value: 'iso27001_scaleway', label: 'ISO 27001:2022 (recommended)' },
        { value: 'secnumcloud_scaleway', label: 'SecNumCloud (ANSSI)' },
        { value: 'nis2_scaleway', label: 'NIS2 European' },
        { value: 'rgpd_scaleway', label: 'RGPD / Infrastructure' },
      ],
      o2switch: [
        { value: 'owasp_top10_o2switch', label: 'OWASP Top 10 (recommended)' },
        { value: 'security_headers_o2switch', label: 'Security Headers Best Practices' },
        { value: 'iso27001_partial_o2switch', label: 'ISO 27001 (web scope)' },
      ],
      unitel: [
        { value: 'iso27001_unitel', label: 'ISO 27001:2022 (recommended)' },
        { value: 'hds_unitel', label: 'HDS — Health Data Host' },
        { value: 'nis2_unitel', label: 'NIS2 European' },
        { value: 'rgpd_unitel', label: 'GDPR — French Sovereign Cloud' },
      ],
    };
    return map[provider] || [];
  },
};

// Subdomains API
export const subdomains = {
  // Discover subdomains for a domain
  discover: async (domain, options = {}) => {
    const response = await apiClient.post('/subdomains/discover', {
      domain,
      use_subfinder: options.use_subfinder !== false,
      use_amass: options.use_amass || false,
      use_dnsrecon: options.use_dnsrecon !== false,
      resolve_ips: options.resolve_ips !== false,
      auto_scan_discovered: options.auto_scan_discovered || false
    }, { timeout: 300000 }); // 5 min timeout for subdomain discovery
    return response.data;
  },
  // Quick subdomain discovery (subfinder only)
  quickDiscover: async (domain) => {
    const response = await apiClient.get(`/subdomains/quick/${encodeURIComponent(domain)}`, {
      timeout: 120000
    });
    return response.data;
  },
  // Discover subdomains for an existing scan
  discoverForScan: async (scanId) => {
    const response = await apiClient.post(`/subdomains/discover/${scanId}`, null, {
      timeout: 300000
    });
    return response.data;
  },
  // Get subdomains for an existing scan
  getForScan: async (scanId) => {
    const response = await apiClient.get(`/subdomains/scan/${scanId}`);
    return response.data;
  },
  // Attack selected subdomains
  attack: async (scanId, selectedSubdomains, scanType = 'quick') => {
    const response = await apiClient.post('/subdomains/attack', {
      scan_id: scanId,
      subdomains: selectedSubdomains,
      scan_type: scanType
    });
    return response.data;
  },
};

// Dynamic Orchestrator API
export const dynamicOrchestrator = {
  // Health check
  health: async () => {
    const response = await apiClient.get('/dynamic-orchestrator/health');
    return response.data;
  },
  // Start a pentest
  start: async (target, scanProfile = 'standard', maxIterations = 100, enabledTools = null) => {
    const response = await apiClient.post('/dynamic-orchestrator/start', {
      target,
      scan_profile: scanProfile,
      max_iterations: maxIterations,
      enabled_tools: enabledTools
    });
    return response.data;
  },
  // Stop pentest
  stop: async () => {
    const response = await apiClient.post('/dynamic-orchestrator/stop');
    return response.data;
  },
  // Get pentest status
  getStatus: async () => {
    const response = await apiClient.get('/dynamic-orchestrator/status');
    return response.data;
  },
  // Get resource usage
  getResources: async () => {
    const response = await apiClient.get('/dynamic-orchestrator/resources');
    return response.data;
  },
  // Get tools status
  getTools: async () => {
    const response = await apiClient.get('/dynamic-orchestrator/tools');
    return response.data;
  },
  // Get dependency graph
  getDependencyGraph: async () => {
    const response = await apiClient.get('/dynamic-orchestrator/dependency-graph');
    return response.data;
  },
  // Get pentest results
  getResults: async () => {
    const response = await apiClient.get('/dynamic-orchestrator/results');
    return response.data;
  },
  // Get statistics
  getStatistics: async () => {
    const response = await apiClient.get('/dynamic-orchestrator/statistics');
    return response.data;
  },
  // Execute single tool with tool-specific timeouts
  // OpenVAS scans can take 1-4 hours, nmap_vuln ~10-30min, others ~10min
  executeTool: async (toolName, target) => {
    // Tool-specific timeout configuration (in milliseconds)
    const TOOL_TIMEOUTS = {
      'openvas': 14400000,     // 4 hours for full OpenVAS vulnerability scan
      'nmap_vuln': 1800000,    // 30 minutes for nmap vuln scripts
      'nikto': 1800000,        // 30 minutes for web vuln scan
      'sqlmap': 3600000,       // 1 hour for SQL injection testing
      'default': 600000        // 10 minutes for standard tools
    };

    const timeout = TOOL_TIMEOUTS[toolName] || TOOL_TIMEOUTS['default'];

    const response = await apiClient.post(`/dynamic-orchestrator/tools/${toolName}/execute`, null, {
      params: { target },
      timeout: timeout
    });
    return response.data;
  },
  // Get config
  getConfig: async () => {
    const response = await apiClient.get('/dynamic-orchestrator/config');
    return response.data;
  },
};

// VPN API
export const vpn = {
  // List all VPN configurations
  // For internal VPNs, pass workspaceId to filter by workspace
  getConfigs: async (vpnType = null, workspaceId = null) => {
    const params = {};
    if (vpnType) params.vpn_type = vpnType;
    if (workspaceId) params.workspace_id = workspaceId;
    const response = await apiClient.get('/vpn/configs', { params });
    return response.data;
  },
  // Get specific config details
  getConfig: async (configId) => {
    const response = await apiClient.get(`/vpn/config/${configId}`);
    return response.data;
  },
  // Upload a new VPN configuration
  // For internal VPNs (interne), workspaceId is REQUIRED
  upload: async (file, vpnType, workspaceId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = { vpn_type: vpnType };
    if (workspaceId) params.workspace_id = workspaceId;
    const response = await apiClient.post('/vpn/upload', formData, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  // Delete a VPN configuration
  delete: async (configId) => {
    const response = await apiClient.delete(`/vpn/config/${configId}`);
    return response.data;
  },
  // Get current VPN connection status
  getStatus: async (workspaceId = null) => {
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await apiClient.get('/vpn/status', { params });
    return response.data;
  },
  // Connect to a VPN
  connect: async (configId, username = null, password = null, workspaceId = null) => {
    const response = await apiClient.post('/vpn/connect', {
      config_id: configId,
      username,
      password,
      workspace_id: workspaceId
    });
    return response.data;
  },
  // Disconnect from VPN
  disconnect: async (workspaceId = null) => {
    const response = await apiClient.post('/vpn/disconnect', {
      workspace_id: workspaceId
    });
    return response.data;
  },
  // Get public IP
  getPublicIp: async () => {
    const response = await apiClient.get('/vpn/public-ip');
    return response.data;
  },
  // Check if OpenVPN is installed
  checkOpenVPN: async () => {
    const response = await apiClient.get('/vpn/check-openvpn');
    return response.data;
  },
  // Save encrypted VPN credentials for a workspace
  saveCredentials: async (vpnConfigId, username, password, workspaceId) => {
    const response = await apiClient.post('/vpn/credentials/save', {
      vpn_config_id: vpnConfigId,
      username,
      password,
      workspace_id: workspaceId
    });
    return response.data;
  },
  // Get VPN credentials info for a workspace (never returns password)
  getCredentials: async (workspaceId) => {
    const response = await apiClient.get(`/vpn/credentials/${workspaceId}`);
    return response.data;
  },
  // Get decrypted VPN credentials for connection
  getDecryptedCredentials: async (workspaceId) => {
    const response = await apiClient.get(`/vpn/credentials/${workspaceId}/decrypt`);
    return response.data;
  },
  // Delete stored VPN credentials
  deleteCredentials: async (workspaceId) => {
    const response = await apiClient.delete(`/vpn/credentials/${workspaceId}`);
    return response.data;
  },
  // Upload multi-protocol VPN configuration (OpenVPN, WireGuard, L2TP)
  // This uses the vpn-protocols endpoint which supports all file types
  uploadMultiProtocol: async (file, workspaceId, protocol = 'openvpn') => {
    const formData = new FormData();
    formData.append('file', file);
    // IMPORTANT: Delete Content-Type to let axios set multipart boundary automatically
    const response = await apiClient.post(
      `/vpn-protocols/workspace/${workspaceId}/upload`,
      formData,
      {
        params: { protocol },
        headers: { 'Content-Type': undefined }  // Override default JSON header for FormData
      }
    );
    return response.data;
  },
  // Check VPN protocols availability (OpenVPN, WireGuard, L2TP)
  checkProtocols: async () => {
    const response = await apiClient.get('/vpn-protocols/protocols/status');
    return response.data;
  },
  // Get workspace VPN config from multi-protocol endpoint
  getWorkspaceConfig: async (workspaceId) => {
    const response = await apiClient.get(`/vpn-protocols/workspace/${workspaceId}/config`);
    return response.data;
  },
  // Update workspace VPN config
  updateWorkspaceConfig: async (workspaceId, configData) => {
    const response = await apiClient.put(`/vpn-protocols/workspace/${workspaceId}/config`, configData);
    return response.data;
  },
  // Connect using multi-protocol service
  connectMultiProtocol: async (workspaceId, credentials = null) => {
    const response = await apiClient.post('/vpn-protocols/connect', {
      workspace_id: workspaceId,
      ...(credentials || {})
    });
    return response.data;
  },
  // Disconnect using multi-protocol service
  disconnectMultiProtocol: async (workspaceId) => {
    const response = await apiClient.post('/vpn-protocols/disconnect', {
      workspace_id: workspaceId
    });
    return response.data;
  },
  // Get connection status from multi-protocol service
  getStatusMultiProtocol: async (workspaceId) => {
    const response = await apiClient.get('/vpn-protocols/status', {
      params: { workspace_id: workspaceId }
    });
    return response.data;
  },
  // ============ VPN Routes Management ============
  // Get custom routes for a workspace
  getRoutes: async (workspaceId) => {
    const response = await apiClient.get(`/vpn/routes/${workspaceId}`);
    // API returns {success, routes, total, workspace_id} - extract routes array
    return response.data?.routes || [];
  },
  // Add a custom route
  addRoute: async (workspaceId, destination, gateway, description = null) => {
    const response = await apiClient.post('/vpn/routes/add', {
      workspace_id: workspaceId,
      destination,
      gateway,
      description
    });
    return response.data;
  },
  // Delete a custom route
  deleteRoute: async (workspaceId, routeId) => {
    const response = await apiClient.delete(`/vpn/routes/${workspaceId}/${routeId}`);
    return response.data;
  },
  // Apply all routes (requires VPN connected)
  applyRoutes: async (workspaceId) => {
    const response = await apiClient.post(`/vpn/routes/${workspaceId}/apply`);
    return response.data;
  },
  // Get network topology info for diagram
  getNetworkInfo: async (workspaceId) => {
    const response = await apiClient.get(`/vpn/network-info/${workspaceId}`);
    return response.data;
  },
};

// Workspaces API
export const workspaces = {
  // List all workspaces
  list: async (skip = 0, limit = 50, search = null, isActive = null) => {
    const params = { skip, limit };
    if (search) params.search = search;
    if (isActive !== null) params.is_active = isActive;
    const response = await apiClient.get('/workspaces', { params });
    return response.data;
  },
  // Get workspace count
  count: async (isActive = null) => {
    const params = {};
    if (isActive !== null) params.is_active = isActive;
    const response = await apiClient.get('/workspaces/count', { params });
    return response.data;
  },
  // Get single workspace
  get: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}`);
    return response.data;
  },
  // Create workspace
  create: async (workspaceData) => {
    const response = await apiClient.post('/workspaces', workspaceData);
    return response.data;
  },
  // Update workspace
  update: async (workspaceId, workspaceData) => {
    const response = await apiClient.put(`/workspaces/${workspaceId}`, workspaceData);
    return response.data;
  },
  // Delete workspace
  delete: async (workspaceId) => {
    const response = await apiClient.delete(`/workspaces/${workspaceId}`);
    return response.data;
  },
  // Get workspace scans - using /scan endpoint for full vulnerability data
  getScans: async (workspaceId, skip = 0, limit = 50) => {
    // Use /scan endpoint instead of /workspaces/{id}/scans to get full vulnerability counts
    const response = await apiClient.get('/scan', {
      params: { workspace_id: workspaceId, offset: skip, limit }
    });
    // Return in the same format as before
    return {
      workspace_id: workspaceId,
      scans: response.data.scans || [],
      total: response.data.total || 0
    };
  },
  // Associate scans to workspace
  associateScans: async (workspaceId, scanIds) => {
    const response = await apiClient.post(`/workspaces/${workspaceId}/scans`, {
      scan_ids: scanIds
    });
    return response.data;
  },
  // Remove scans from workspace
  removeScans: async (workspaceId, scanIds) => {
    const response = await apiClient.delete(`/workspaces/${workspaceId}/scans`, {
      data: { scan_ids: scanIds }
    });
    return response.data;
  },
  // Get workspace stats
  getStats: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/stats`);
    return response.data;
  },
  // Generate contract
  generateContract: async (workspaceId, templateType = 'pentest_standard') => {
    const response = await apiClient.post(`/workspaces/${workspaceId}/contract/generate`, {
      template_type: templateType
    });
    return response.data;
  },
  // Sign contract
  signContract: async (workspaceId, token, signerName, signerEmail) => {
    const response = await apiClient.post(`/workspaces/${workspaceId}/contract/sign`, null, {
      params: { token, signer_name: signerName, signer_email: signerEmail }
    });
    return response.data;
  },
  // Get contract status
  getContractStatus: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/contract/status`);
    return response.data;
  },
  // Lookup company by SIREN
  lookupSiren: async (siren) => {
    const response = await apiClient.get(`/workspaces/pappers/lookup/siren/${siren}`);
    return response.data;
  },
  // Lookup company by SIRET
  lookupSiret: async (siret) => {
    const response = await apiClient.get(`/workspaces/pappers/lookup/siret/${siret}`);
    return response.data;
  },
  // Search Pappers by company name
  searchPappers: async (query) => {
    const response = await apiClient.get('/workspaces/pappers/search', { params: { q: query } });
    return response.data;
  },
  // Get AI summary for workspace
  getAiSummary: async (workspaceId, forceRefresh = false) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/ai-summary`, {
      params: { force_refresh: forceRefresh },
      timeout: 90000  // 90s timeout for AI generation
    });
    return response.data;
  },
  // Get complete AI analysis with solutions for workspace
  getAiAnalysis: async (workspaceId, regenerate = false) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/ai-analysis`, {
      params: { regenerate },
      timeout: 180000  // 3min timeout for complete AI analysis
    });
    return response.data;
  },
  // Get documents and reports for workspace
  getDocuments: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/documents`);
    return response.data;
  },
  // Get attack tree / workflow for workspace
  getAttackTree: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/attack-tree`, {
      timeout: 60000  // 60s timeout
    });
    return response.data;
  },
};

// Pappers API (French company lookup)
export const pappers = {
  // Lookup company by SIREN (9 digits) - France only
  lookupBySiren: async (siren) => {
    const response = await apiClient.get(`/workspaces/pappers/lookup/siren/${siren}`);
    return response.data;
  },
  // Lookup company by SIRET (14 digits) - France only
  lookupBySiret: async (siret) => {
    const response = await apiClient.get(`/workspaces/pappers/lookup/siret/${siret}`);
    return response.data;
  },
  // Search companies by name - France only
  searchByName: async (name, city = null) => {
    const params = { name };
    if (city) params.city = city;
    const response = await apiClient.get('/workspaces/pappers/search', { params });
    return response.data;
  },
};

// Multi-country Business Registry API
export const businessRegistry = {
  // Get supported countries
  getCountries: async () => {
    const response = await apiClient.get('/workspaces/registry/countries');
    return response.data;
  },
  // Lookup company by country and identifier
  lookup: async (countryCode, identifier) => {
    const response = await apiClient.get(`/workspaces/registry/lookup/${countryCode}/${identifier}`);
    return response.data;
  },
  // Search companies by country and name
  search: async (countryCode, name, city = null, limit = 10) => {
    const params = { name, limit };
    if (city) params.city = city;
    const response = await apiClient.get(`/workspaces/registry/search/${countryCode}`, { params });
    return response.data;
  },
};

// Vulnerabilities API
export const vulnerabilities = {
  // Update vulnerability priority
  updatePriority: async (vulnId, customPriority, priorityNote = null) => {
    const response = await apiClient.patch(`/scan/vulnerability/${vulnId}/priority`, {
      custom_priority: customPriority,
      priority_note: priorityNote
    });
    return response.data;
  },
  // Mark/unmark vulnerability as false positive
  setFalsePositive: async (vulnId, isFalsePositive, reason = null) => {
    const response = await apiClient.patch(`/scan/vulnerability/${vulnId}/false-positive`, {
      is_false_positive: isFalsePositive,
      reason: reason
    });
    return response.data;
  },
};

// ============================================
// SaaS API Endpoints
// ============================================

// Billing API
export const billing = {
  // Get available plans
  getPlans: async () => {
    const response = await apiClient.get('/billing/plans');
    return response.data;
  },
  // Get current subscription
  getSubscription: async () => {
    const response = await apiClient.get('/billing/subscription');
    return response.data;
  },
  // Create subscription
  createSubscription: async (plan, billingInterval = 'monthly') => {
    const response = await apiClient.post('/billing/subscription', {
      plan,
      billing_interval: billingInterval
    });
    return response.data;
  },
  // Cancel subscription
  cancelSubscription: async () => {
    const response = await apiClient.delete('/billing/subscription');
    return response.data;
  },
  // Create audit payment (pay-per-audit)
  createAuditPayment: async () => {
    const response = await apiClient.post('/billing/audit-payment');
    return response.data;
  },
  // Get invoices
  getInvoices: async (limit = 50) => {
    const response = await apiClient.get('/billing/invoices', { params: { limit } });
    return response.data;
  },
  // Get billing portal URL
  getPortalUrl: async () => {
    const response = await apiClient.get('/billing/portal-url');
    return response.data;
  },
  // Get usage stats
  getUsage: async () => {
    const response = await apiClient.get('/billing/usage');
    return response.data;
  },
  // Create Stripe Checkout Session
  createCheckoutSession: async (plan, billingInterval = 'monthly') => {
    const response = await apiClient.post('/billing/create-checkout-session', {
      plan,
      billing_interval: billingInterval
    });
    return response.data;
  },
};

// Account API (Mon Compte)
export const account = {
  // Get profile
  getProfile: async () => {
    const response = await apiClient.get('/account/profile');
    return response.data;
  },
  // Update profile
  updateProfile: async (profileData) => {
    const response = await apiClient.patch('/account/profile', profileData);
    return response.data;
  },
  // Change password
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.post('/account/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },
  // MFA Setup
  setupMfa: async () => {
    const response = await apiClient.post('/account/mfa/setup');
    return response.data;
  },
  // Enable MFA
  enableMfa: async (code) => {
    const response = await apiClient.post('/account/mfa/enable', { code });
    return response.data;
  },
  // Disable MFA
  disableMfa: async (code) => {
    const response = await apiClient.post('/account/mfa/disable', { code });
    return response.data;
  },
  // Get MFA status
  getMfaStatus: async () => {
    const response = await apiClient.get('/account/mfa/status');
    return response.data;
  },
  // Regenerate backup codes
  regenerateBackupCodes: async (code) => {
    const response = await apiClient.post('/account/mfa/regenerate-backup-codes', { code });
    return response.data;
  },
  // Get notification settings
  getNotifications: async () => {
    const response = await apiClient.get('/account/notifications');
    return response.data;
  },
  // Update notification settings
  updateNotifications: async (settings) => {
    const response = await apiClient.patch('/account/notifications', settings);
    return response.data;
  },
  // Generate API key
  generateApiKey: async () => {
    const response = await apiClient.post('/account/api-key');
    return response.data;
  },
  // Get API key info
  getApiKeyInfo: async () => {
    const response = await apiClient.get('/account/api-key');
    return response.data;
  },
  // Revoke API key
  revokeApiKey: async () => {
    const response = await apiClient.delete('/account/api-key');
    return response.data;
  },
  // Get preferences
  getPreferences: async () => {
    const response = await apiClient.get('/account/preferences');
    return response.data;
  },
  // Update preferences
  updatePreferences: async (preferences) => {
    const response = await apiClient.patch('/account/preferences', preferences);
    return response.data;
  },
  // Get sessions
  getSessions: async () => {
    const response = await apiClient.get('/account/sessions');
    return response.data;
  },
  // Update avatar
  updateAvatar: async (avatarData) => {
    const response = await apiClient.post('/account/avatar', { avatar_data: avatarData });
    return response.data;
  },
  // Get API key (alias for getApiKeyInfo)
  getApiKey: async () => {
    const response = await apiClient.get('/account/api-key');
    return response.data;
  },
  // Update company
  updateCompany: async (companyData) => {
    const response = await apiClient.patch('/account/company', companyData);
    return response.data;
  },
  // Update company logo
  updateCompanyLogo: async (logoData) => {
    const response = await apiClient.post('/account/company/logo', { logo_data: logoData });
    return response.data;
  },
  // Delete company logo
  deleteCompanyLogo: async () => {
    const response = await apiClient.delete('/account/company/logo');
    return response.data;
  },
  // Get team members
  getTeam: async () => {
    const response = await apiClient.get('/account/team');
    return response.data;
  },
  // Invite team member
  inviteTeamMember: async (inviteData) => {
    const response = await apiClient.post('/account/team/invite', inviteData);
    return response.data;
  },
  // Update team member
  updateTeamMember: async (userId, data) => {
    const response = await apiClient.patch(`/account/team/${userId}`, data);
    return response.data;
  },
  // Remove team member
  removeTeamMember: async (userId) => {
    const response = await apiClient.delete(`/account/team/${userId}`);
    return response.data;
  },
  // Resend invitation
  resendInvitation: async (userId) => {
    const response = await apiClient.post(`/account/team/${userId}/resend-invitation`);
    return response.data;
  },
};

// Support API
export const support = {
  // Get tickets
  getTickets: async (status = null, limit = 50) => {
    const params = { limit };
    if (status) params.status = status;
    const response = await apiClient.get('/support/tickets', { params });
    return response.data;
  },
  // Get single ticket
  getTicket: async (ticketId) => {
    const response = await apiClient.get(`/support/tickets/${ticketId}`);
    return response.data;
  },
  // Create ticket
  createTicket: async (ticketData) => {
    const response = await apiClient.post('/support/tickets', ticketData);
    return response.data;
  },
  // Add message to ticket
  addMessage: async (ticketId, message, attachments = []) => {
    const response = await apiClient.post(`/support/tickets/${ticketId}/messages`, {
      message,
      attachments
    });
    return response.data;
  },
  // Close ticket
  closeTicket: async (ticketId, satisfactionRating = null, feedback = null) => {
    const params = {};
    if (satisfactionRating) params.satisfaction_rating = satisfactionRating;
    if (feedback) params.satisfaction_feedback = feedback;
    const response = await apiClient.post(`/support/tickets/${ticketId}/close`, null, { params });
    return response.data;
  },
};

// Admin API (Super Admin only)
export const admin = {
  // Get platform stats
  getStats: async () => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },
  // Get companies
  getCompanies: async (status = null, plan = null, search = null, limit = 50, offset = 0) => {
    const params = { limit, offset };
    if (status) params.status = status;
    if (plan) params.plan = plan;
    if (search) params.search = search;
    const response = await apiClient.get('/admin/companies', { params });
    return response.data;
  },
  // Get company details
  getCompany: async (companyId) => {
    const response = await apiClient.get(`/admin/companies/${companyId}`);
    return response.data;
  },
  // Update company
  updateCompany: async (companyId, data) => {
    const response = await apiClient.patch(`/admin/companies/${companyId}`, data);
    return response.data;
  },
  // Delete company
  deleteCompany: async (companyId) => {
    const response = await apiClient.delete(`/admin/companies/${companyId}`);
    return response.data;
  },
  // Get users
  getUsers: async (role = null, isActive = null, limit = 50, offset = 0) => {
    const params = { limit, offset };
    if (role) params.role = role;
    if (isActive !== null) params.is_active = isActive;
    const response = await apiClient.get('/admin/users', { params });
    return response.data;
  },
  // Update user
  updateUser: async (userId, data) => {
    const response = await apiClient.patch(`/admin/users/${userId}`, data);
    return response.data;
  },
  // Get audit logs
  getAuditLogs: async (companyId = null, actionCategory = null, limit = 100, offset = 0) => {
    const params = { limit, offset };
    if (companyId) params.company_id = companyId;
    if (actionCategory) params.action_category = actionCategory;
    const response = await apiClient.get('/admin/audit-logs', { params });
    return response.data;
  },
  // Get suspicious activity
  getSuspiciousActivity: async (limit = 100) => {
    const response = await apiClient.get('/admin/audit-logs/suspicious', { params: { limit } });
    return response.data;
  },
};

// Company Registration
export const registration = {
  // Register new company
  registerCompany: async (data) => {
    const response = await apiClient.post('/auth/register-company', data);
    return response.data;
  },
  // Verify email
  verifyEmail: async (token) => {
    const response = await apiClient.post(`/auth/verify-email/${token}`);
    return response.data;
  },
  // Forgot password
  forgotPassword: async (email) => {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },
  // Reset password
  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      new_password: newPassword
    });
    return response.data;
  },
};

// Monitoring API
export const monitoring = {
  // Get system health
  getHealth: async () => {
    const response = await apiClient.get('/monitoring/health');
    return response.data;
  },
  // Get detailed resource usage
  getResources: async () => {
    const response = await apiClient.get('/monitoring/resources');
    return response.data;
  },
  // Get scan metrics
  getScanMetrics: async () => {
    const response = await apiClient.get('/monitoring/scans/metrics');
    return response.data;
  },
  // Get network isolation status
  getNetworkStatus: async () => {
    const response = await apiClient.get('/monitoring/network/status');
    return response.data;
  },
  // Get GPU status
  getGpuStatus: async () => {
    const response = await apiClient.get('/monitoring/gpus');
    return response.data;
  },
  // Get hashcat status
  getHashcatStatus: async () => {
    const response = await apiClient.get('/monitoring/hashcat/status');
    return response.data;
  },
  // Get checkpoints
  getCheckpoints: async (scanId = null) => {
    const params = scanId ? { scan_id: scanId } : {};
    const response = await apiClient.get('/monitoring/checkpoints', { params });
    return response.data;
  },
  // Get active alerts
  getAlerts: async (severity = null, limit = 50) => {
    const params = { limit };
    if (severity) params.severity = severity;
    const response = await apiClient.get('/monitoring/alerts', { params });
    return response.data;
  },
  // Get running processes
  getProcesses: async (filterPattern = null) => {
    const params = filterPattern ? { filter_pattern: filterPattern } : {};
    const response = await apiClient.get('/monitoring/processes', { params });
    return response.data;
  },
  // Get Prometheus metrics (raw)
  getMetrics: async () => {
    const response = await apiClient.get('/monitoring/metrics', {
      responseType: 'text'
    });
    return response.data;
  },
};

// Schedules API
export const schedules = {
  // List schedules for a workspace
  list: async (workspaceId, skip = 0, limit = 50, isActive = null) => {
    const params = { skip, limit };
    if (workspaceId) params.workspace_id = workspaceId;
    if (isActive !== null) params.is_active = isActive;
    const response = await apiClient.get('/schedules', { params });
    return response.data;
  },
  // Get single schedule
  get: async (scheduleId) => {
    const response = await apiClient.get(`/schedules/${scheduleId}`);
    return response.data;
  },
  // Create schedule
  create: async (scheduleData) => {
    const response = await apiClient.post('/schedules', scheduleData);
    return response.data;
  },
  // Update schedule
  update: async (scheduleId, scheduleData) => {
    const response = await apiClient.put(`/schedules/${scheduleId}`, scheduleData);
    return response.data;
  },
  // Delete schedule
  delete: async (scheduleId) => {
    const response = await apiClient.delete(`/schedules/${scheduleId}`);
    return response.data;
  },
  // Pause schedule
  pause: async (scheduleId) => {
    const response = await apiClient.post(`/schedules/${scheduleId}/pause`);
    return response.data;
  },
  // Resume schedule
  resume: async (scheduleId) => {
    const response = await apiClient.post(`/schedules/${scheduleId}/resume`);
    return response.data;
  },
  // Run schedule now
  runNow: async (scheduleId) => {
    const response = await apiClient.post(`/schedules/${scheduleId}/run-now`);
    return response.data;
  },
  // Get schedule executions
  getExecutions: async (scheduleId, skip = 0, limit = 20) => {
    const response = await apiClient.get(`/schedules/${scheduleId}/executions`, {
      params: { skip, limit }
    });
    return response.data;
  },
};

// Credentials API (Dark Web / Breach Data)
export const credentials = {
  // Get all credentials (with optional workspace filter)
  getAll: async (workspaceId = null) => {
    const params = {};
    if (workspaceId) params.workspace_id = workspaceId;
    const response = await apiClient.get('/credentials/all', { params });
    return response.data;
  },
  // Get credentials stats (with optional workspace filter)
  getStats: async (workspaceId = null) => {
    const params = {};
    if (workspaceId) params.workspace_id = workspaceId;
    const response = await apiClient.get('/credentials/stats', { params });
    return response.data;
  },
  // Search by domain (with optional workspace_id to tag results)
  searchDomain: async (domain, workspaceId = null) => {
    const payload = { domain };
    if (workspaceId) payload.workspace_id = workspaceId;
    const response = await apiClient.post('/credentials/search-domain', payload);
    return response.data;
  },
  // Validate credentials for report
  validateForReport: async (credentialIds, validated = true) => {
    const response = await apiClient.post('/credentials/validate-for-report', {
      credential_ids: credentialIds,
      validated
    });
    return response.data;
  },
  // Get validated credentials
  getValidated: async () => {
    const response = await apiClient.get('/credentials/validated');
    return response.data;
  },
  // ============================================
  // Dictionary Management
  // ============================================
  // Save a dictionary
  saveDictionary: async (data) => {
    const response = await apiClient.post('/credentials/dictionaries', data);
    return response.data;
  },
  // List all dictionaries
  listDictionaries: async (workspaceId = null) => {
    const params = {};
    if (workspaceId) params.workspace_id = workspaceId;
    const response = await apiClient.get('/credentials/dictionaries', { params });
    return response.data;
  },
  // Get a dictionary
  getDictionary: async (dictionaryId) => {
    const response = await apiClient.get(`/credentials/dictionaries/${dictionaryId}`);
    return response.data;
  },
  // Delete a dictionary
  deleteDictionary: async (dictionaryId) => {
    const response = await apiClient.delete(`/credentials/dictionaries/${dictionaryId}`);
    return response.data;
  },
  // Crack a hashed password
  crackHash: async (credentialId) => {
    const response = await apiClient.post(`/credentials/crack-credential-hash/${credentialId}`);
    return response.data;
  },
  // Crack all hashes for a domain
  crackAllHashes: async (domain = null, limit = 50) => {
    const response = await apiClient.post('/credentials/crack-all-hashes', null, {
      params: { domain, limit }
    });
    return response.data;
  },
};

// Exploit Sessions API (AD Attacks)
export const exploitSessions = {
  // Get sessions for a scan
  getForScan: async (scanId) => {
    const response = await apiClient.get(`/exploit/sessions/${scanId}`);
    return response.data;
  },
  // Validate sessions for report
  validateForReport: async (sessionIds, validated = true) => {
    const response = await apiClient.post('/exploit/sessions/validate-for-report', {
      session_ids: sessionIds,
      validated
    });
    return response.data;
  },
  // Get validated sessions
  getValidated: async () => {
    const response = await apiClient.get('/exploit/sessions/validated');
    return response.data;
  },
};

// Report Templates API
export const reportTemplates = {
  // Get all report types
  getTypes: async (language = 'fr') => {
    const response = await apiClient.get('/report-templates/types', { params: { language } });
    return response.data;
  },
  // Get sections for a report type
  getTypeSections: async (reportType, language = 'fr') => {
    const response = await apiClient.get(`/report-templates/types/${reportType}/sections`, {
      params: { language }
    });
    return response.data;
  },
  // Get all templates
  getTemplates: async (reportType = null, language = null, includeDefaults = true) => {
    const params = { include_defaults: includeDefaults };
    if (reportType) params.report_type = reportType;
    if (language) params.language = language;
    const response = await apiClient.get('/report-templates/templates', { params });
    return response.data;
  },
  // Get single template
  getTemplate: async (templateId) => {
    const response = await apiClient.get(`/report-templates/templates/${templateId}`);
    return response.data;
  },
  // Create template
  createTemplate: async (templateData) => {
    const response = await apiClient.post('/report-templates/templates', templateData);
    return response.data;
  },
  // Update template
  updateTemplate: async (templateId, templateData) => {
    const response = await apiClient.patch(`/report-templates/templates/${templateId}`, templateData);
    return response.data;
  },
  // Delete template
  deleteTemplate: async (templateId) => {
    const response = await apiClient.delete(`/report-templates/templates/${templateId}`);
    return response.data;
  },
  // Duplicate template
  duplicateTemplate: async (templateId, newName) => {
    const response = await apiClient.post(`/report-templates/templates/${templateId}/duplicate`, null, {
      params: { new_name: newName }
    });
    return response.data;
  },
  // Get report settings
  getSettings: async () => {
    const response = await apiClient.get('/report-templates/settings');
    return response.data;
  },
  // Update report settings
  updateSettings: async (settings) => {
    const response = await apiClient.patch('/report-templates/settings', settings);
    return response.data;
  },
};

// User Settings API (Multi-tenant preferences)
export const userSettings = {
  // Get current user settings
  get: async () => {
    const response = await apiClient.get('/user-settings');
    return response.data;
  },
  // Get default settings structure
  getDefaults: async () => {
    const response = await apiClient.get('/user-settings/defaults');
    return response.data;
  },
  // Update all settings
  updateAll: async (settings) => {
    const response = await apiClient.put('/user-settings', settings);
    return response.data;
  },
  // Update notification settings
  updateNotifications: async (notificationSettings) => {
    const response = await apiClient.put('/user-settings/notifications', {
      notification_settings: notificationSettings
    });
    return response.data;
  },
  // Update integration settings
  updateIntegrations: async (integrationSettings) => {
    const response = await apiClient.put('/user-settings/integrations', {
      integration_settings: integrationSettings
    });
    return response.data;
  },
  // Update UI preferences
  updateUIPreferences: async (uiPreferences) => {
    const response = await apiClient.put('/user-settings/ui-preferences', {
      ui_preferences: uiPreferences
    });
    return response.data;
  },
  // Update license settings
  updateLicense: async (licenseSettings) => {
    const response = await apiClient.put('/user-settings/license', {
      license_settings: licenseSettings
    });
    return response.data;
  },
  // Update custom settings
  updateCustom: async (customSettings) => {
    const response = await apiClient.put('/user-settings/custom', {
      custom_settings: customSettings
    });
    return response.data;
  },
  // Reset settings to defaults
  reset: async () => {
    const response = await apiClient.delete('/user-settings');
    return response.data;
  },
};

// Tool Config API (Persistent tool activation)
export const toolConfig = {
  // Get all tool configurations
  getAll: async () => {
    const response = await apiClient.get('/tool-config');
    return response.data;
  },
  // Get list of enabled tools
  getEnabled: async () => {
    const response = await apiClient.get('/tool-config/enabled');
    return response.data;
  },
  // Get config for a specific tool
  get: async (toolName) => {
    const response = await apiClient.get(`/tool-config/${encodeURIComponent(toolName)}`);
    return response.data;
  },
  // Toggle a single tool
  toggle: async (toolName, isEnabled) => {
    const response = await apiClient.post('/tool-config/toggle', {
      tool_name: toolName,
      is_enabled: isEnabled
    });
    return response.data;
  },
  // Bulk toggle multiple tools
  bulkToggle: async (tools) => {
    const response = await apiClient.post('/tool-config/bulk-toggle', { tools });
    return response.data;
  },
  // Enable all specified tools
  enableAll: async (tools) => {
    const response = await apiClient.post('/tool-config/enable-all', tools);
    return response.data;
  },
  // Disable all specified tools
  disableAll: async (tools) => {
    const response = await apiClient.post('/tool-config/disable-all', tools);
    return response.data;
  },
  // Update tool config
  update: async (toolName, config) => {
    const response = await apiClient.put(`/tool-config/${encodeURIComponent(toolName)}`, config);
    return response.data;
  },
  // Delete tool config (reset to default)
  delete: async (toolName) => {
    const response = await apiClient.delete(`/tool-config/${encodeURIComponent(toolName)}`);
    return response.data;
  },
};

// Credits API (Credit system for subscriptions)
export const credits = {
  // Get credit summary for current user/company
  getSummary: async () => {
    const response = await apiClient.get('/credits/summary');
    return response.data;
  },
  // Get available credit plans
  getPlans: async () => {
    const response = await apiClient.get('/credits/plans');
    return response.data;
  },
  // Get available credit packs
  getPacks: async () => {
    const response = await apiClient.get('/credits/packs');
    return response.data;
  },
  // Get credit costs for actions
  getCosts: async () => {
    const response = await apiClient.get('/credits/costs');
    return response.data;
  },
  // Check if can consume credits for action
  canConsume: async (actionType) => {
    const response = await apiClient.get(`/credits/can-consume/${actionType}`);
    return response.data;
  },
  // Get credit balances
  getBalances: async () => {
    const response = await apiClient.get('/credits/balances');
    return response.data;
  },
  // Get credit transactions history
  getTransactions: async (params = {}) => {
    const response = await apiClient.get('/credits/transactions', { params });
    return response.data;
  },
  // Get credit warnings
  getWarnings: async () => {
    const response = await apiClient.get('/credits/warnings');
    return response.data;
  },
  // Purchase a credit pack
  purchasePack: async (packSlug, stripePaymentMethodId = null) => {
    const response = await apiClient.post('/credits/purchase-pack', {
      pack_slug: packSlug,
      stripe_payment_method_id: stripePaymentMethodId
    });
    return response.data;
  },
  // Subscribe to a credit plan
  subscribe: async (planSlug, stripePaymentMethodId = null) => {
    const response = await apiClient.post('/credits/subscribe', {
      plan_slug: planSlug,
      stripe_payment_method_id: stripePaymentMethodId
    });
    return response.data;
  },
};

// History Actions API (User activity tracking)
export const historyActions = {
  // Track a single action
  track: async (action) => {
    const response = await apiClient.post('/history/track', action);
    return response.data;
  },
  // Track multiple actions (batch)
  trackBatch: async (actions) => {
    const response = await apiClient.post('/history/batch', actions);
    return response.data;
  },
  // Get action history with filters
  getActions: async (params = {}) => {
    const response = await apiClient.get('/history/actions', { params });
    return response.data;
  },
  // Get action statistics
  getStats: async (days = 7, userId = null) => {
    const response = await apiClient.get('/history/stats', {
      params: { days, user_id: userId }
    });
    return response.data;
  },
  // Get actions for a specific user
  getUserActions: async (userId, params = {}) => {
    const response = await apiClient.get(`/history/user/${userId}`, { params });
    return response.data;
  },
  // Cleanup old actions (admin only)
  cleanup: async (days = 90) => {
    const response = await apiClient.delete('/history/cleanup', { params: { days } });
    return response.data;
  },
};

// Contracts API
export const contracts = {
  // Create a new contract
  create: async (contractData) => {
    const response = await apiClient.post('/contracts/', contractData);
    return response.data;
  },
  // List contracts for a workspace
  list: async (workspaceId) => {
    const response = await apiClient.get(`/contracts/workspace/${workspaceId}`);
    return response.data;
  },
  // Get a specific contract
  get: async (contractId) => {
    const response = await apiClient.get(`/contracts/${contractId}`);
    return response.data;
  },
  // Send contract for signature
  send: async (contractId) => {
    const response = await apiClient.post(`/contracts/${contractId}/send`);
    return response.data;
  },
  // Download PDF
  downloadPdf: async (contractId) => {
    const response = await apiClient.get(`/contracts/${contractId}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },
  // Download signature proof (ZIP with all signature data)
  downloadSignatureProof: async (contractId) => {
    const response = await apiClient.get(`/contracts/${contractId}/signature-proof`, {
      responseType: 'blob'
    });
    return response.data;
  },
  // Update a contract (draft or pending_signature only)
  update: async (contractId, updateData) => {
    const response = await apiClient.put(`/contracts/${contractId}`, updateData);
    return response.data;
  },
  // Delete a draft contract
  delete: async (contractId) => {
    const response = await apiClient.delete(`/contracts/${contractId}`);
    return response.data;
  },
  // Get access logs for a contract (timeline)
  getAccessLogs: async (contractId) => {
    const response = await apiClient.get(`/contracts/${contractId}/access-logs`);
    return response.data;
  },
  // Get the latest contract for a workspace (for quick status check)
  getLatest: async (workspaceId) => {
    const response = await apiClient.get(`/contracts/workspace/${workspaceId}`);
    const contracts = response.data;
    if (contracts && contracts.length > 0) {
      // Return the most recent contract
      return contracts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    }
    return null;
  },
  // Get active/signed contracts for a workspace (valid date range)
  getActiveForWorkspace: async (workspaceId) => {
    const response = await apiClient.get(`/contracts/active-for-workspace/${workspaceId}`);
    return response.data;
  },
  // Validate contract for scan
  validateForScan: async (workspaceId, target) => {
    const response = await apiClient.post('/contracts/validate-for-scan', { workspace_id: workspaceId, target });
    return response.data;
  },
  // Templates
  listTemplates: async (workspaceId) => {
    const response = await apiClient.get(`/contracts/templates/workspace/${workspaceId}`);
    return response.data;
  },
  createTemplate: async (templateData) => {
    const response = await apiClient.post('/contracts/templates/', templateData);
    return response.data;
  },
  updateTemplate: async (templateId, templateData) => {
    const response = await apiClient.put(`/contracts/templates/${templateId}`, templateData);
    return response.data;
  },
  deleteTemplate: async (templateId) => {
    const response = await apiClient.delete(`/contracts/templates/${templateId}`);
    return response.data;
  },
};

// Generated Reports API (LLM-powered report generation)
export const generatedReports = {
  // Get available report types
  getTypes: async () => {
    const response = await apiClient.get('/generated-reports/types');
    return response.data;
  },
  // Create a new report
  create: async (reportData) => {
    const response = await apiClient.post('/generated-reports/', reportData);
    return response.data;
  },
  // List reports
  list: async (params = {}) => {
    const response = await apiClient.get('/generated-reports/', { params });
    return response.data;
  },
  // Get a specific report
  get: async (reportId) => {
    const response = await apiClient.get(`/generated-reports/${reportId}`);
    return response.data;
  },
  // Get report generation status
  getStatus: async (reportId) => {
    const response = await apiClient.get(`/generated-reports/${reportId}/status`);
    return response.data;
  },
  // Regenerate a report
  regenerate: async (reportId, sections = null) => {
    const response = await apiClient.post(`/generated-reports/${reportId}/regenerate`, {
      sections_to_regenerate: sections
    });
    return response.data;
  },
  // Export report to specified format
  export: async (reportId, format, sendEmail = false, includeAttachments = false) => {
    const response = await apiClient.post(`/generated-reports/${reportId}/export`, {
      format,
      send_email: sendEmail,
      include_attachments: includeAttachments
    });
    return response.data;
  },
  // Download exported report
  download: async (reportId, format) => {
    const response = await apiClient.get(`/generated-reports/${reportId}/download/${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },
  // Delete a report
  delete: async (reportId) => {
    const response = await apiClient.delete(`/generated-reports/${reportId}`);
    return response.data;
  },
};

// Servers API (Multi-server/VPS monitoring)
export const servers = {
  // List all servers
  list: async (includeInactive = false, serverType = null) => {
    const params = { include_inactive: includeInactive };
    if (serverType) params.server_type = serverType;
    const response = await apiClient.get('/servers', { params });
    return response.data;
  },
  // Get server stats
  getStats: async () => {
    const response = await apiClient.get('/servers/stats');
    return response.data;
  },
  // Create a new server
  create: async (data) => {
    const response = await apiClient.post('/servers', data);
    return response.data;
  },
  // Get server by ID
  get: async (serverId) => {
    const response = await apiClient.get(`/servers/${serverId}`);
    return response.data;
  },
  // Update server
  update: async (serverId, data) => {
    const response = await apiClient.patch(`/servers/${serverId}`, data);
    return response.data;
  },
  // Delete server
  delete: async (serverId) => {
    const response = await apiClient.delete(`/servers/${serverId}`);
    return response.data;
  },
  // Refresh metrics for a server
  refresh: async (serverId) => {
    const response = await apiClient.post(`/servers/${serverId}/refresh`);
    return response.data;
  },
  // Refresh all servers
  refreshAll: async () => {
    const response = await apiClient.post('/servers/refresh-all');
    return response.data;
  },
  // Ping a server
  ping: async (serverId) => {
    const response = await apiClient.post(`/servers/${serverId}/ping`);
    return response.data;
  },

  // === Resources ===

  // List resources for a server
  listResources: async (serverId, resourceType = null) => {
    const params = resourceType ? { resource_type: resourceType } : {};
    const response = await apiClient.get(`/servers/${serverId}/resources`, { params });
    return response.data;
  },

  // Create a resource
  createResource: async (serverId, data) => {
    const response = await apiClient.post(`/servers/${serverId}/resources`, data);
    return response.data;
  },

  // Update a resource
  updateResource: async (serverId, resourceId, data) => {
    const response = await apiClient.patch(`/servers/${serverId}/resources/${resourceId}`, data);
    return response.data;
  },

  // Delete a resource
  deleteResource: async (serverId, resourceId) => {
    const response = await apiClient.delete(`/servers/${serverId}/resources/${resourceId}`);
    return response.data;
  },

  // List all resources across all servers
  listAllResources: async (resourceType = null, status = null) => {
    const params = {};
    if (resourceType) params.resource_type = resourceType;
    if (status) params.status = status;
    const response = await apiClient.get('/servers/resources/all', { params });
    return response.data;
  },

  // Get resource stats
  getResourcesStats: async () => {
    const response = await apiClient.get('/servers/resources/stats');
    return response.data;
  },

  // === Docker Containers ===

  // List containers for a server
  listContainers: async (serverId, status = null) => {
    const params = status ? { status } : {};
    const response = await apiClient.get(`/servers/${serverId}/containers`, { params });
    return response.data;
  },

  // Sync containers from a server
  syncContainers: async (serverId) => {
    const response = await apiClient.post(`/servers/${serverId}/containers/sync`);
    return response.data;
  },

  // Restart a container
  restartContainer: async (serverId, containerId) => {
    const response = await apiClient.post(`/servers/${serverId}/containers/${containerId}/restart`);
    return response.data;
  },

  // List all containers across all servers
  listAllContainers: async (status = null) => {
    const params = status ? { status } : {};
    const response = await apiClient.get('/servers/containers/all', { params });
    return response.data;
  },

  // Get container stats
  getContainersStats: async () => {
    const response = await apiClient.get('/servers/containers/stats');
    return response.data;
  },
};

// OSINT API - Breach checking and enrichment
export const osint = {
  // Health check
  health: async () => {
    const response = await apiClient.get('/osint/health');
    return response.data;
  },

  // List available tools
  tools: async () => {
    const response = await apiClient.get('/osint/tools');
    return response.data;
  },

  // Check email breaches (HIBP)
  checkEmailBreaches: async (email, comprehensive = true, storeResults = true, scanId = null) => {
    const response = await apiClient.post('/osint/hibp/email', {
      email,
      comprehensive,
      store_results: storeResults,
      scan_id: scanId
    });
    return response.data;
  },

  // Check password exposure (HIBP)
  checkPassword: async (password) => {
    const response = await apiClient.post('/osint/hibp/password', { password });
    return response.data;
  },

  // Check domain breaches
  checkDomainBreaches: async (domain, harvestEmails = true) => {
    const response = await apiClient.post('/osint/hibp/domain', {
      domain,
      harvest_emails: harvestEmails
    });
    return response.data;
  },

  // Discover email accounts (Holehe)
  discoverAccounts: async (email) => {
    const response = await apiClient.post('/osint/holehe', { email });
    return response.data;
  },

  // Harvest domain (theHarvester)
  harvestDomain: async (domain) => {
    const response = await apiClient.post('/osint/harvester', { domain });
    return response.data;
  },

  // Investigate username (Maigret)
  investigateUsername: async (username, topSites = 300) => {
    const response = await apiClient.post('/osint/maigret', {
      username,
      top_sites: topSites
    });
    return response.data;
  },

  // Run SpiderFoot scan
  spiderFootScan: async (target, scanType = 'passive', modules = null) => {
    const response = await apiClient.post('/osint/spiderfoot', {
      target,
      scan_type: scanType,
      modules
    });
    return response.data;
  },

  // Comprehensive check
  comprehensiveCheck: async (email = null, username = null, domain = null, scanId = null) => {
    const response = await apiClient.post('/osint/comprehensive', {
      email,
      username,
      domain,
      scan_id: scanId
    });
    return response.data;
  },

  // Bulk email check
  bulkEmailCheck: async (emails, scanId = null) => {
    const response = await apiClient.post('/osint/bulk/emails', {
      emails,
      scan_id: scanId
    });
    return response.data;
  },

  // Get task status
  getTaskStatus: async (taskId) => {
    const response = await apiClient.get(`/osint/task/${taskId}`);
    return response.data;
  },

  // Generate remediation plan
  generateRemediationPlan: async (email) => {
    const response = await apiClient.post('/osint/remediation-plan', null, {
      params: { email }
    });
    return response.data;
  },

  // === Enrichment ===

  // Enrich scan credentials
  enrichScan: async (scanId, checkPasswords = false, domain = null) => {
    const response = await apiClient.post('/osint/enrichment/scan', {
      scan_id: scanId,
      check_passwords: checkPasswords,
      domain
    });
    return response.data;
  },

  // Enrich internal/AD scan
  enrichInternalScan: async (scanId, domain) => {
    const response = await apiClient.post('/osint/enrichment/internal', {
      scan_id: scanId,
      domain
    });
    return response.data;
  },

  // Get data types
  getDataTypes: async () => {
    const response = await apiClient.get('/osint/enrichment/data-types');
    return response.data;
  },

  // Get scan enrichment results
  getScanEnrichment: async (scanId) => {
    const response = await apiClient.get(`/osint/enrichment/scan/${scanId}`);
    return response.data;
  },
};

// AI Agent Supervisor API
export const aiAgent = {
  // Configuration
  getConfig: async () => {
    const response = await apiClient.get('/ai-agent/config');
    return response.data;
  },
  updateConfig: async (config) => {
    const response = await apiClient.put('/ai-agent/config', config);
    return response.data;
  },
  resetConfig: async () => {
    const response = await apiClient.post('/ai-agent/config/reset');
    return response.data;
  },

  // Status and Control
  getStatus: async () => {
    const response = await apiClient.get('/ai-agent/status');
    return response.data;
  },
  start: async () => {
    const response = await apiClient.post('/ai-agent/control/start');
    return response.data;
  },
  stop: async () => {
    const response = await apiClient.post('/ai-agent/control/stop');
    return response.data;
  },
  restart: async () => {
    const response = await apiClient.post('/ai-agent/control/restart');
    return response.data;
  },

  // Actions
  getActions: async (params = {}) => {
    const response = await apiClient.get('/ai-agent/actions', { params });
    return response.data;
  },
  getAction: async (actionId) => {
    const response = await apiClient.get(`/ai-agent/actions/${actionId}`);
    return response.data;
  },
  getActionsStats: async (period = '24h') => {
    const response = await apiClient.get('/ai-agent/actions/stats', { params: { period } });
    return response.data;
  },

  // Incidents
  getIncidents: async (params = {}) => {
    const response = await apiClient.get('/ai-agent/incidents', { params });
    return response.data;
  },
  getIncident: async (incidentId) => {
    const response = await apiClient.get(`/ai-agent/incidents/${incidentId}`);
    return response.data;
  },
  acknowledgeIncident: async (incidentId, notes = null) => {
    const response = await apiClient.post(`/ai-agent/incidents/${incidentId}/acknowledge`, { notes });
    return response.data;
  },
  resolveIncident: async (incidentId, resolutionNotes = null) => {
    const response = await apiClient.post(`/ai-agent/incidents/${incidentId}/resolve`, { resolution_notes: resolutionNotes });
    return response.data;
  },
  retryRecovery: async (incidentId) => {
    const response = await apiClient.post(`/ai-agent/incidents/${incidentId}/retry-recovery`);
    return response.data;
  },

  // Metrics
  getMetrics: async (period = '1h', resolution = '1m') => {
    const response = await apiClient.get('/ai-agent/metrics', { params: { period, resolution } });
    return response.data;
  },
  getCurrentMetrics: async () => {
    const response = await apiClient.get('/ai-agent/metrics/current');
    return response.data;
  },

  // Reports
  getReports: async (params = {}) => {
    const response = await apiClient.get('/ai-agent/reports', { params });
    return response.data;
  },
  getReport: async (reportDate) => {
    const response = await apiClient.get(`/ai-agent/reports/${reportDate}`);
    return response.data;
  },
  getReportDetail: async (reportDate) => {
    const response = await apiClient.get(`/ai-agent/reports/${reportDate}`);
    return response.data;
  },
  generateReport: async (reportDate = null) => {
    const response = await apiClient.post('/ai-agent/reports/generate', { report_date: reportDate });
    return response.data;
  },
  sendReport: async (reportDate, recipients) => {
    const response = await apiClient.post(`/ai-agent/reports/${reportDate}/send`, { recipients });
    return response.data;
  },

  // AI Models
  getModels: async () => {
    const response = await apiClient.get('/ai-agent/models');
    return response.data;
  },
  getModelsStats: async (period = '24h') => {
    const response = await apiClient.get('/ai-agent/models/stats', { params: { period } });
    return response.data;
  },
  testModel: async (modelName) => {
    const response = await apiClient.post(`/ai-agent/models/${modelName}/test`);
    return response.data;
  },

  // Manual Actions
  triggerAnalysis: async (target = null) => {
    const response = await apiClient.post('/ai-agent/actions/analyze', { target });
    return response.data;
  },
  healthCheck: async () => {
    const response = await apiClient.post('/ai-agent/actions/health-check');
    return response.data;
  },
};

// AI Assistant API
export const aiAssistantAPI = {
  // Chat
  sendMessage: async (data) => {
    const response = await apiClient.post('/ai-assistant/chat', data);
    return response.data;
  },

  // Conversations
  listConversations: async (params = {}) => {
    const response = await apiClient.get('/ai-assistant/conversations', { params });
    return response.data;
  },
  getConversation: async (conversationId) => {
    const response = await apiClient.get(`/ai-assistant/conversations/${conversationId}`);
    return response.data;
  },
  deleteConversation: async (conversationId) => {
    const response = await apiClient.delete(`/ai-assistant/conversations/${conversationId}`);
    return response.data;
  },
  archiveConversation: async (conversationId) => {
    const response = await apiClient.post(`/ai-assistant/conversations/${conversationId}/archive`);
    return response.data;
  },

  // Feedback
  submitFeedback: async (messageId, feedback) => {
    const response = await apiClient.post(`/ai-assistant/messages/${messageId}/feedback`, feedback);
    return response.data;
  },

  // Actions
  getActions: async (pageContext = null, category = null) => {
    const params = {};
    if (pageContext) params.page_context = pageContext;
    if (category) params.category = category;
    const response = await apiClient.get('/ai-assistant/actions', { params });
    return response.data;
  },
  executeAction: async (data) => {
    const response = await apiClient.post('/ai-assistant/actions/execute', data);
    return response.data;
  },

  // Knowledge
  getPageKnowledge: async (pagePath) => {
    const response = await apiClient.get(`/ai-assistant/knowledge/${pagePath}`);
    return response.data;
  },

  // Status
  getStatus: async () => {
    const response = await apiClient.get('/ai-assistant/status');
    return response.data;
  },

  // Autonomous actions
  executeAutonomousAction: async (actionData) => {
    const response = await apiClient.post('/ai-assistant/actions/execute-autonomous', actionData);
    return response.data;
  },

  // Workflow status
  getWorkflowStatus: async () => {
    const response = await apiClient.get('/ai-assistant/workflows/status');
    return response.data;
  },
};

// Connectors API (admin)
export const connectorsAPI = {
  list: async () => {
    const response = await apiClient.get('/connectors/list');
    return response.data;
  },
  update: async (connectorId, data) => {
    const response = await apiClient.put(`/connectors/${connectorId}`, data);
    return response.data;
  },
  healthCheck: async () => {
    const response = await apiClient.get('/connectors/health');
    return response.data;
  },
};

// Remediation Tracking API
export const remediationTracking = {
  getDashboard: async (workspaceId) => {
    const response = await apiClient.get(`/remediation/tracking/${workspaceId}`);
    return response.data;
  },
  getVulns: async (workspaceId, params = {}) => {
    const response = await apiClient.get(`/remediation/tracking/${workspaceId}/vulns`, { params });
    return response.data;
  },
  updateStatus: async (vulnId, data) => {
    const response = await apiClient.patch(`/vulnerabilities/${vulnId}`, data);
    return response.data;
  },
  triggerRescan: async (vulnId) => {
    const response = await apiClient.post(`/remediation/rescan/${vulnId}`);
    return response.data;
  },
};

// Generic API request function for custom endpoints
export const apiRequest = async (url, options = {}) => {
  const { method = 'GET', body, ...restOptions } = options;

  const config = {
    method,
    ...restOptions
  };

  if (body) {
    config.data = body;
  }

  // Handle URL - if it starts with /api/v1, use baseURL logic
  const finalUrl = url.startsWith('/api/v1') ? url.replace('/api/v1', '') : url;

  const response = await apiClient.request({
    url: finalUrl,
    ...config
  });

  return response.data;
};

export default apiClient;
